import Button from "components/button";
import ButtonIcon from "components/button-icon";
import Loading from "components/center-loading";
import Checkbox from "components/checkbox";
import { IconTrash } from "components/icons/icn-index.icon";
import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { formatDeliveryTime } from "utils/delivery-time";
import { formatNumber } from "utils/format";
import { DATA_FORMAT_TYPES, getDataFormat } from "utils/format-date";
import { formatErrorMessage } from "utils/handle-error";
import {
  cancelStockReturn,
  fetchCurrentStockReturns,
  finishStockReturns,
} from "../../stocks-detail.service";
import * as S from "./styled";
import type {
  DeconsignmentProps,
  StockReturn,
  StockReturnCapabilities,
  StockReturnsResponse,
} from "./types";

const EMPTY_VALUE = "-";

const displayValue = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? EMPTY_VALUE : value;

const getErrorMessage = (error: unknown) =>
  formatErrorMessage(error) || intl.get("stocks.stockDetail.deconsignment.error");

const Deconsignment = ({
  labeledFamilyCenterId,
  reloadKey = 0,
  onFeedback,
}: DeconsignmentProps) => {
  const [items, setItems] = useState<StockReturn[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [itemToCancel, setItemToCancel] = useState<StockReturn | null>(null);
  const [capabilities, setCapabilities] = useState<StockReturnCapabilities>({
    CanFinish: false,
    CanCancel: false,
  });

  const loadStockReturns = useCallback(async () => {
    if (!Number.isFinite(labeledFamilyCenterId)) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = (await fetchCurrentStockReturns(
        labeledFamilyCenterId
      )) as StockReturnsResponse;
      setItems(response.Items ?? []);
      setCapabilities(response.Capabilities ?? { CanFinish: false, CanCancel: false });
      setSelectedIds([]);
    } catch (requestError) {
      setItems([]);
      setCapabilities({ CanFinish: false, CanCancel: false });
      setSelectedIds([]);
      setError(getErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [labeledFamilyCenterId]);

  // reloadKey intentionally forces a refresh when the parent reports an external stock-return change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is an explicit refresh trigger.
  useEffect(() => {
    loadStockReturns();
  }, [loadStockReturns, reloadKey]);

  const finishableIds = useMemo(
    () => (capabilities.CanFinish ? items.map((item) => item.StockReturnId) : []),
    [capabilities.CanFinish, items]
  );

  const allFinishableSelected =
    finishableIds.length > 0 && finishableIds.every((id) => selectedIds.includes(id));

  const selectedItem =
    selectedIds.length === 1
      ? (items.find((item) => item.StockReturnId === selectedIds[0]) ?? null)
      : null;

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? finishableIds : []);
  };

  const toggleSelected = (item: StockReturn, checked: boolean) => {
    if (!capabilities.CanFinish) return;

    setSelectedIds((currentIds) =>
      checked
        ? Array.from(new Set([...currentIds, item.StockReturnId]))
        : currentIds.filter((id) => id !== item.StockReturnId)
    );
  };

  const handleFinish = async () => {
    if (!selectedIds.length || isFinishing) return;

    setIsFinishing(true);
    try {
      await finishStockReturns(selectedIds);
      onFeedback?.(intl.get("stocks.stockDetail.deconsignment.finishSuccess"));
      await loadStockReturns();
    } catch (requestError) {
      onFeedback?.(getErrorMessage(requestError));
    } finally {
      setIsFinishing(false);
    }
  };

  const handleCancel = async () => {
    if (!itemToCancel || cancellingId) return;

    const stockReturnId = itemToCancel.StockReturnId;
    setCancellingId(stockReturnId);

    try {
      await cancelStockReturn(stockReturnId);
      setItemToCancel(null);
      onFeedback?.(intl.get("stocks.stockDetail.deconsignment.cancelSuccess"));
      await loadStockReturns();
    } catch (requestError) {
      onFeedback?.(getErrorMessage(requestError));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <S.Wrapper data-testid="deconsignment-section">
      <S.Subtitle>{intl.get("stocks.stockDetail.deconsignment.stockReturn")}</S.Subtitle>
      <Loading isLoading={isLoading || isFinishing || Boolean(cancellingId)} fullHeightParent />

      {error && <S.ErrorState role="alert">{error}</S.ErrorState>}

      {!error && items.length === 0 && !isLoading ? (
        <S.EmptyState>
          <p>{intl.get("stocks.emptyList.stockReturn")}</p>
        </S.EmptyState>
      ) : (
        <>
          <S.TableWrapper>
            <S.Table>
              <thead>
                <tr>
                  <S.HeaderCell style={{ width: "48px" }}>
                    <Checkbox
                      name="stock-return-checkbox-all"
                      checked={allFinishableSelected}
                      disabled={!finishableIds.length || isFinishing || Boolean(cancellingId)}
                      onChange={(event) => toggleAll(event.target.checked)}
                      aria-label={intl.get("stocks.stockDetail.deconsignment.selectAll")}
                    />
                  </S.HeaderCell>
                  <S.HeaderCell style={{ width: "60px" }}>
                    {intl.get("stocks.stockDetail.deconsignment.status")}
                  </S.HeaderCell>
                  <S.HeaderCell style={{ width: "35px" }}>
                    {intl.get("stocks.stockDetail.deconsignment.type")}
                  </S.HeaderCell>
                  <S.HeaderCell>{intl.get("stocks.stockDetail.deconsignment.source")}</S.HeaderCell>
                  <S.HeaderCell>
                    {intl.get("stocks.stockDetail.deconsignment.destination")}
                  </S.HeaderCell>
                  <S.HeaderCell>
                    {intl.get("stocks.stockDetail.deconsignment.quantity")}
                  </S.HeaderCell>
                  <S.HeaderCell>
                    {intl.get("stocks.stockDetail.deconsignment.deliveryDate")}
                  </S.HeaderCell>
                  <S.HeaderCell>{intl.get("stocks.stockDetail.deconsignment.time")}</S.HeaderCell>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isSelected = selectedIds.includes(item.StockReturnId);

                  return (
                    <tr key={item.StockReturnId}>
                      <S.CheckboxCell>
                        <Checkbox
                          name={`stock-return-checkbox-${item.StockReturnId}`}
                          checked={isSelected}
                          disabled={!capabilities.CanFinish || isFinishing || Boolean(cancellingId)}
                          onChange={(event) => toggleSelected(item, event.target.checked)}
                          aria-label={`${intl.get("stocks.stockDetail.deconsignment.select")} ${item.StockReturnId}`}
                        />
                      </S.CheckboxCell>
                      <S.Cell>{displayValue(item.Status)}</S.Cell>
                      <S.Cell>{displayValue(item.SuggestionType)}</S.Cell>
                      <S.Cell title={item.Source}>{displayValue(item.Source)}</S.Cell>
                      <S.Cell title={item.Destination}>{displayValue(item.Destination)}</S.Cell>
                      <S.Cell>
                        {formatNumber(item.Quantity)} {displayValue(item.UnitOfMeasure)}
                      </S.Cell>
                      <S.Cell>
                        {getDataFormat(item.DeliveryDate, DATA_FORMAT_TYPES.WITH_YEAR)}
                      </S.Cell>
                      <S.Cell>{formatDeliveryTime(item.DeliveryTime) || EMPTY_VALUE}</S.Cell>
                    </tr>
                  );
                })}
              </tbody>
            </S.Table>
          </S.TableWrapper>
          <S.Footer>
            <div title={intl.get("stocks.stockDetail.deconsignment.cancel")}>
              <ButtonIcon
                aria-label={intl.get("stocks.stockDetail.deconsignment.cancel")}
                buttonIcon={
                  <IconTrash width={18} height={18} color="var(--color-contrast-white)" />
                }
                buttonType="danger"
                style={{ width: "50px" }}
                disabled={
                  !capabilities.CanCancel || !selectedItem || isFinishing || Boolean(cancellingId)
                }
                onClick={() => selectedItem && setItemToCancel(selectedItem)}
              />
            </div>
            <Button
              value={intl.get("stocks.stockDetail.deconsignment.finish")}
              buttonType="primary"
              disabled={!selectedIds.length || isFinishing || Boolean(cancellingId)}
              onClick={handleFinish}
              style={{ width: "200px" }}
            />
          </S.Footer>
        </>
      )}

      {itemToCancel && (
        <S.Overlay open aria-modal="true" aria-labelledby="deconsignment-cancel-dialog-title">
          <S.Dialog>
            <S.DialogTitle id="deconsignment-cancel-dialog-title">
              {intl.get("stocks.stockDetail.deconsignment.confirmCancelTitle")}
            </S.DialogTitle>
            <S.DialogMessage>
              {intl.get("stocks.stockDetail.deconsignment.confirmCancel")}
            </S.DialogMessage>
            <S.DialogActions>
              <Button
                value={intl.get("stocks.stockDetail.deconsignment.close")}
                buttonType="default"
                disabled={Boolean(cancellingId)}
                onClick={() => setItemToCancel(null)}
                style={{ width: "120px" }}
              />
              <Button
                value={intl.get("stocks.stockDetail.deconsignment.cancel")}
                buttonType="danger"
                disabled={Boolean(cancellingId)}
                onClick={handleCancel}
                style={{ width: "160px" }}
              />
            </S.DialogActions>
          </S.Dialog>
        </S.Overlay>
      )}
    </S.Wrapper>
  );
};

export { Deconsignment };
export default Deconsignment;
